import React from 'react';
import { Container, Card, ListGroup, Badge, Row, Col } from 'react-bootstrap';
import { Clock, CheckCircle, Settings } from 'lucide-react'; 
import { getCurrencySymbol } from '../../libs/countryData';

const msgMap = {
  ordered: "Your order is being confirmed.",
  preparing: "Your order is being prepared.",
  ready_to_serve: "Your order is ready. A staff member will bring it to your table.",
  served: "Enjoy your meal!",
};

// Helper function (same as before) to calculate the total price for a single item
const calculateItemTotal = (item) => {
    const modifierPriceDelta = item.chosenModifiers.reduce((totalDelta, group) => {
        return totalDelta + group.selections.reduce((groupDelta, selection) => {
            return groupDelta + (selection.priceDelta || 0);
        }, 0);
    }, 0);

    const pricePerUnit = item.basePrice + modifierPriceDelta;
    return pricePerUnit * item.qty;
};

// Helper function to get an icon based on status
const getStatusIcon = (status) => {
    switch (status) {
        case 'ordered':
            return <Clock size={20} className="me-2 text-warning" />;
        case 'preparing':
            return <Settings size={20} className="me-2 text-info" />;
        case 'served':
        case 'ready_to_serve':
            return <CheckCircle size={20} className="me-2 text-success" />;
        default:
            return null;
    }
};

const OrderWaitingScreen = ({ tableSession }) => {
    if (!tableSession || !tableSession.items || tableSession.items.length === 0) {
        return (
            <Container className="p-4">
                <p className="text-center text-muted">Awaiting order details...</p>
            </Container>
        );
    }

    const grandTotal = tableSession.items.reduce((total, item) => total + calculateItemTotal(item), 0);
    const statusText = tableSession.status.replaceAll('_', ' ').toUpperCase();

    return (
        <Container className="p-0 pb-5" style={{ maxWidth: '600px' }}>
            {/* Header Card for Status and Table Info */}
            <Card className="shadow-lg mb-4 text-center border-0 rounded-0">
                <Card.Body className="bg-primary text-white py-3">
                    <h5 className="mb-0">
                        {getStatusIcon(tableSession.status)}
                        Order Status: <strong className="text-white">{statusText}</strong>
                    </h5>
                </Card.Body>
                <Card.Footer className="text-muted small">
                    <h6>{msgMap[tableSession.status]}</h6>
                    <hr className='my-2' />
                    Table: <strong>{tableSession.tableLabel || tableSession.tableId}</strong> | Order #{tableSession.orderRef || 'N/A'}
                </Card.Footer>
            </Card>

            <h3 className="mb-3 px-3">Your Order ({tableSession.items.length})</h3>

            <ListGroup variant="flush">
                {tableSession.items.map((item, index) => (
                    <ListGroup.Item key={index} className="px-3 py-3 border-bottom shadow-sm mb-2 rounded-lg">
                        <Row className="align-items-center">
                            {/* 1. Item Image (using a mock image URL for demonstration) */}
                            <Col xs={3}>
                                <div 
                                    className="bg-light rounded"
                                    style={{ 
                                        width: '100%', 
                                        paddingTop: '100%', 
                                        backgroundImage: `url(${item.imageUrl || '/placeholder.jpg'})`, // Assume imageUrl
                                        backgroundSize: 'cover', 
                                        backgroundPosition: 'center' 
                                    }}
                                ></div>
                            </Col>

                            {/* 2. Item Details and Modifiers */}
                            <Col xs={6}>
                                <h5 className="mb-1 text-truncate">
                                    <Badge bg="secondary" className="me-2">x{item.qty}</Badge>
                                    <strong>{item.name}</strong>
                                </h5>
                                
                                {/* Display Modifiers */}
                                {item.chosenModifiers.map((group, groupIndex) => (
                                    <div key={groupIndex} className="small text-muted mt-1">
                                        <small>
                                            {group.groupName}: 
                                            {group.selections.map((selection, selectionIndex) => (
                                                <Badge pill bg="light" text="dark" className="ms-1" key={selectionIndex}>
                                                    {selection.name}
                                                    {selection.priceDelta > 0 && ` (+${selection.priceDelta.toFixed(2)})`}
                                                </Badge>
                                            ))}
                                        </small>
                                    </div>
                                ))}
                            </Col>

                            {/* 3. Item Total Price */}
                            <Col xs={3} className="text-end">
                                <h5 className="text-success mb-0">
                                    <small>{getCurrencySymbol()}</small><strong>{calculateItemTotal(item).toFixed(2)}</strong>
                                </h5>
                            </Col>
                        </Row>
                    </ListGroup.Item>
                ))}
            </ListGroup>

            {/* Grand Total Footer */}
            <Card className="fixed-bottom border-0 rounded-0 shadow-lg mt-4 d-none d-md-block"> {/* Hidden on mobile to save space */}
                <Card.Body className="py-2 bg-light">
                    <Row>
                        <Col className="text-end">
                            <h4 className="mb-0">
                                Total: <strong className="text-primary">{getCurrencySymbol()}{grandTotal.toFixed(2)}</strong>
                            </h4>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
            
             {/* Small fixed footer for mobile */}
            <div className="fixed-bottom bg-white p-3 border-top d-md-none shadow-lg">
                 <Row className='align-items-center'>
                     <Col xs={6}>
                         <h6 className='mb-0 text-muted'>Grand Total:</h6>
                     </Col>
                     <Col xs={6} className='text-end'>
                         <h4 className="mb-0 text-primary">
                             <strong>{getCurrencySymbol()}{grandTotal.toFixed(2)}</strong>
                         </h4>
                     </Col>
                 </Row>
            </div>
            
        </Container>
    );
};

export default OrderWaitingScreen;